import React, { useState, useContext } from "react";
import {
  GoAButton,
  GoAModal,
  GoAInputTel,
  GoAInputText,
  GoABlock,
  GoAFormItem,
  GoASpacer,
} from "@abgov/react-components";
import useCsrService from "../services/csr-api-redux";
import { OtpUser } from "../common/models";
import { Phone } from "../models/sms";

import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../state/store";
import { useDispatch, useSelector } from "react-redux";
import { loginUser, sendOtp } from "../state/slices/authSlice";

interface OtpProps {
  use_as_login?: boolean;
}

const OtpComponent = ({ use_as_login = false }: OtpProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const csrService = useCsrService();

  const [showLoginState, setModalLoginOpen] = useState(use_as_login);
  const [modalRegisterOpen, setRegisterModalOpen] = useState(!use_as_login);
  const [formVisible, setFormVisible] = useState(false);

  const [isCodeInError, setIsCodeInError] = useState(false);
  const [otpInErrorText, setOtpInErrorText] = useState("");
  const [usernameInErrorText, setUsernameInErrorText] = useState("");

  const [username, setUsername] = useState("");
  const [otpText, setOtpText] = useState("");
  const [otpUser, setUser] = useState<OtpUser>();
  const [smsNumber, setSmsNumber] = useState<Phone>(new Phone(""));

  let navigate = useNavigate();

  const setOtpInError = (isInError: boolean) => {
    setIsCodeInError(isInError);
    isInError ? setOtpInErrorText("Invalid otp code") : setOtpInErrorText("");
  };

  const getSmsErrorText = () => {
    if (smsNumber && smsNumber.toString().length > 0 && !smsNumber.isValid()) {
      return smsNumber.getErrorText();
    }

    return "";
  };

  const handlePasteClick = async (event: any) => {
    event.preventDefault();

    const paste = await navigator.clipboard.readText();
    handleOtpChange(event, paste);
  };

  const handleUsernameInputChange = (event: any, value: string) => {
    setUsername(value);
  };

  const handleOtpChange = (event: any, value: any) => {
    const user = otpUser;
    otpUser!.otp = value;
    setUser(user);
    setOtpText(value);
    setOtpInError(false);
  };

  const handleSmsInputChange = (event: any, value: string) => {
    const sms = new Phone(value);
    setSmsNumber(sms);
  };

  const handleRegisterSmsOnClick = async () => {
    if (smsNumber && smsNumber.isValid()) {
      const smsCreateRequest = {
        action: "create",
        sms_number: smsNumber.cleanPhoneNumber(),
      };

      return csrService
        .post("/api/v1/users/", smsCreateRequest)
        .then((response) => {
          var optUser = {
            otp: response.data.opt,
            username: response.data.username,
          } as OtpUser;

          setUser(optUser);
          setRegisterModalOpen(false);
          setFormVisible(true);
        });
    }
  };

  const handleContinueOnClick = async () => {
    try {
      if (otpUser) {
        setOtpInError(false);
        return dispatch(loginUser({ csrService, otpUser })).then((response) => {
          if (response.meta.requestStatus === "fulfilled") {
            navigate(`/email/${otpUser.username}`);
          } else {
            setOtpInError(true);
          }
        });
      }
    } catch (error) {
      setOtpInError(true);
    }
  };

  const handleOtpRenew = async () => {
    if ((otpUser && otpUser.username) || username) {
      const renewRequest = {
        username: otpUser?.username ?? username,
      };

      return dispatch(sendOtp({ csrService, otpUser: renewRequest })).then(
        (response) => {
          var otpUser = {
            username: response.payload.username,
          } as OtpUser;

          setUser(otpUser);
          setModalLoginOpen(false);
          setFormVisible(true);
        }
      );
    }
  };

  const handleSignOnClick = async () => {
    try {
      setUsernameInErrorText("");
      return await handleOtpRenew();
    } catch (error) {
      setUsernameInErrorText("Error with Guest ID login process");
    }
  };

  const handleResendOnClick = async (event: any) => {
    event.preventDefault();
    setOtpText("");
    return handleOtpRenew();
  };

  return (
    <main>
      <div style={{ display: formVisible ? "block" : "none" }}>
        <GoABlock direction="column" alignment="start" gap="none">
          <div>
            <h1>Check your phone</h1>
          </div>
          <div>
            We've sent you a text message to {smsNumber?.hidePhoneNumber()} with
            a one time verification code.
          </div>
          <goa-spacer vspacing={"s"}></goa-spacer>
          <div>
            <GoAFormItem
              error={otpInErrorText}
              label="Enter your code to login"
            >
              <GoAInputText
                name="otp"
                value={otpText}
                error={isCodeInError}
                leadingContent={React.createElement(
                  "div",
                  {
                    onClick: handlePasteClick,
                    style: {
                      cursor: "pointer",
                    },
                  },
                  "Paste"
                )}
                onChange={handleOtpChange}
              ></GoAInputText>
            </GoAFormItem>
          </div>
          <goa-spacer vspacing={"m"}></goa-spacer>
          <div>
            <GoAButton onClick={handleContinueOnClick} disabled={!otpText}>
              Continue
            </GoAButton>
          </div>
          <goa-spacer vspacing={"m"}></goa-spacer>
          <div>
            <a href="" onClick={handleResendOnClick}>
              Resend verification code
            </a>
          </div>
        </GoABlock>
      </div>
      <div style={{ display: showLoginState ? "block" : "none" }}>
        <GoASpacer vSpacing={"2xl"} />
        <GoABlock direction="column">
          <div>
            <h2>Sign in with guest ID number</h2>
          </div>
          <GoASpacer vSpacing={"l"} />
          <GoAFormItem
            error={usernameInErrorText}
            requirement="required"
            label="Enter guest ID number"
          >
            <GoAInputText
              id="username"
              name="username"
              onChange={handleUsernameInputChange}
            ></GoAInputText>
          </GoAFormItem>
          <div>
            Enter the xx digit ID number you previously saved and emailed
            yourself
          </div>
          <GoASpacer vSpacing={"xl"} />
          <div>
            <GoAButton onClick={handleSignOnClick}>Sign In</GoAButton>
          </div>
          <GoASpacer vSpacing={"4xl"} />
        </GoABlock>
      </div>
      <GoAModal role="dialog" open={modalRegisterOpen}>
        <GoABlock direction="column">
          <div>
            <h1>Verify your identity</h1>
          </div>
          <div>
            Provide us with your mobile number and we'll send you a guest token
            ID via SMS text.
          </div>
          <div>
            <GoAFormItem error={getSmsErrorText()} label="SMS Number">
              <GoAInputTel
                id="sms"
                name="sms"
                error={smsNumber.toString().length > 0 && !smsNumber.isValid()}
                value={smsNumber?.formattedPhoneNumber()}
                onChange={handleSmsInputChange}
                leadingContent="1"
              ></GoAInputTel>
            </GoAFormItem>
          </div>
          <div>
            <GoAButton
              onClick={handleRegisterSmsOnClick}
              testId="request-guest-token-button"
              disabled={!smsNumber.isValid()}
            >
              Request Guest Token
            </GoAButton>
          </div>
          <div>
            <GoAButton
              type="tertiary"
              onClick={() => {
                setRegisterModalOpen(false);
                let path = "/";
                navigate(path);
              }}
              testId="cancel-guest-token-button"
            >
              Cancel
            </GoAButton>
          </div>
        </GoABlock>
      </GoAModal>
    </main>
  );
};

export default OtpComponent;
